/**
 * @file SeoMainTab.tsx
 * @description Вкладка «Основное» раздела «Сайт → SEO» (phase19.16, партия
 * Р3в, v0.19.64; SB145, SB44, SB45, SEO1, SEO16; uiux-r3 §3.5): четыре
 * карточки, перенесённые из конструктора, — «Заголовки и описание»,
 * «Картинка для ссылок», «Индексация», «Подтверждение сайта». Каждая
 * сохраняется сама и сразу (без публикации вида сайта); у полей —
 * «изменено … · кто» и «Вернуть прежнее». Над карточками — плашка
 * неопубликованных SEO-правок черновика, если они остались после переноса.
 *
 * Раскладка как была в конструкторе: «Заголовки» во всю ширину, ниже две
 * независимые колонки с 48rem ширины (на 360 и 768 — одна колонка).
 * Без права настроек — только просмотр с пояснением.
 *
 * «SEO-техника» v0.19.65 (SEO8, SB146; uiux-seo-tech §3.2, §3.6): новый
 * порядок — в DOM карточки идут по важности, поэтому на 360 и 768
 * «Индексация» и IndexNow выше картинки и подтверждения: «Заголовки» во всю
 * ширину; с `@3xl` слева «Индексация» → «Сообщать поисковикам об
 * изменениях», справа «Картинка для ссылок» → «Подтверждение сайта»; внизу
 * во всю ширину — «Разметка для поиска» (только чтение).
 */

import { useState } from "react";

import { ListError } from "../../ui/ListStates";
import { Skeleton } from "../../ui/skeleton";
import { SeoHistoryDialog } from "./SeoHistoryDialog";
import { SeoIndexingCard } from "./SeoIndexingCard";
import { SeoIndexNowCard } from "./SeoIndexNowCard";
import { SeoLinkImageCard } from "./SeoLinkImageCard";
import { SeoMarkupCard } from "./SeoMarkupCard";
import { SeoPendingNotice } from "./SeoPendingNotice";
import { SeoTitlesCard } from "./SeoTitlesCard";
import { SeoVerificationCard } from "./SeoVerificationCard";
import { useCurrentContext, useHasPermission } from "../../../auth/hooks";
import { useShopSeoSettings } from "../../../hooks/site-seo/useShopSeoSettings";
import { useShopSiteUrl } from "../../../hooks/useShopSiteUrl";
import type { ShopSeoFieldKey } from "../../../lib/api-site-seo";
import { SEO_APPLY_NOTE } from "../../../lib/site-seo/seo-fields";

export function SeoMainTab() {
  const ctx = useCurrentContext();
  const readOnly = !useHasPermission("SHOP_MANAGE_SETTINGS");
  const api = useShopSeoSettings();
  const siteUrl = useShopSiteUrl();
  const [historyField, setHistoryField] = useState<ShopSeoFieldKey | null>(null);
  const { settings } = api;

  if (api.loading && !settings) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true" aria-label="Загрузка настроек SEO">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-40 rounded-xl" />
        ))}
      </div>
    );
  }
  if (!settings) return <ListError message={api.loadError ?? "Не удалось загрузить настройки SEO."} onRetry={() => void api.reload()} />;

  const siteName = ctx?.shop?.displayName || "Магазин";
  const card = { api, settings, onHistory: setHistoryField, readOnly };
  return (
    <div className="@container flex min-w-0 flex-col gap-4">
      <p className="type-caption text-on-surface-variant">
        {readOnly ? "Настройки SEO меняет владелец магазина или сотрудник с правом настроек магазина." : SEO_APPLY_NOTE}
      </p>
      <SeoPendingNotice api={api} readOnly={readOnly} />
      <SeoTitlesCard {...card} siteName={siteName} siteUrl={siteUrl} />
      <div className="grid min-w-0 items-start gap-4 @3xl:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-4">
          <SeoIndexingCard {...card} siteUrl={siteUrl} />
          <SeoIndexNowCard {...card} />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <SeoLinkImageCard {...card} siteName={siteName} shopSlug={ctx?.shop?.slug ?? ""} />
          <SeoVerificationCard {...card} />
        </div>
      </div>
      <SeoMarkupCard settings={settings} />
      <SeoHistoryDialog field={historyField} api={api} onClose={() => setHistoryField(null)} />
    </div>
  );
}
